import {
  Button,
  FileInput,
  Modal,
  Select,
  TextInput,
  Textarea,
} from "flowbite-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { EventSponsor } from "../../../Models/EventSponsor";
import { Event } from "../../../Models/Event";
import { Member } from "../../../Models/Member";
import EventSponsorApi from "../../../Api/EventSponsorApi";
import EventApi from "../../../Api/EventApi";
import CommonApi from "../../../Api/CommonApi";
import PublicCompanyApi, {
  PublicCompanyDto,
  PublicCompanyCreateRequest,
} from "../../../Api/PublicCompanyApi";
import { fetchMembers } from "../../../Actions/CommonFields";
import { useAppDispatch, useAppSelector } from "../../../Reducers/store";

type SponsorType = "member" | "publicCompany" | "custom";

export default function AddEditEventSponsorModal({
  show,
  setShow,
  sponsor,
  onSaved,
}: {
  show: boolean;
  setShow: (v: boolean) => void;
  sponsor: EventSponsor | null;
  onSaved: () => void;
}) {
  const dispatch = useAppDispatch();
  const { members } = useAppSelector((state) => state.commonFields);
  const [events, setEvents] = useState<Event[]>([]);
  const [publicCompanies, setPublicCompanies] = useState<PublicCompanyDto[]>(
    []
  );
  const [sponsorType, setSponsorType] = useState<SponsorType>("custom");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<EventSponsor>>({
    eventId: 0,
    memberId: null,
    publicCompanyId: null,
    name: "",
    sponsorAmount: null,
    information: "",
    sponsorImageUrl: "",
  });

  useEffect(() => {
    if (show) {
      EventApi.getEventList().then((r) => setEvents(r.data));
      PublicCompanyApi.getAll().then((r) => setPublicCompanies(r.data));
      dispatch(fetchMembers());
    }
  }, [show, dispatch]);

  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming = events.filter((ev) => {
      if (!ev.eventDate) return false;
      const d = new Date(ev.eventDate);
      return !isNaN(d.getTime()) && d >= today;
    });
    if (sponsor?.eventId && !upcoming.some((ev) => ev.id === sponsor.eventId)) {
      const currentEvent = events.find((ev) => ev.id === sponsor.eventId);
      if (currentEvent) return [currentEvent, ...upcoming];
    }
    return upcoming;
  }, [events, sponsor?.eventId]);

  useEffect(() => {
    if (sponsor) {
      setFormData({
        id: sponsor.id,
        eventId: sponsor.eventId,
        memberId: sponsor.memberId ?? null,
        publicCompanyId: sponsor.publicCompanyId ?? null,
        name: sponsor.name,
        sponsorAmount: sponsor.sponsorAmount ?? null,
        information: sponsor.information ?? "",
        sponsorImageUrl: sponsor.sponsorImageUrl ?? "",
      });
      setSponsorType(
        sponsor.memberId
          ? "member"
          : sponsor.publicCompanyId
          ? "publicCompany"
          : "custom"
      );
    } else {
      setFormData({
        eventId: upcomingEvents[0]?.id ?? 0,
        memberId: null,
        publicCompanyId: null,
        name: "",
        sponsorAmount: null,
        information: "",
        sponsorImageUrl: "",
      });
      setSponsorType("custom");
      setFile(null);
    }
  }, [sponsor, events, upcomingEvents]);

  const handleMemberChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value ? parseInt(e.target.value, 10) : null;
    setFormData((p) => ({ ...p, memberId: id }));
    if (id) {
      const m = members.find((x) => x.id === id);
      if (m) setFormData((p) => ({ ...p, name: m.name ?? "" }));
    }
  };

  const handlePublicCompanyChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const id = e.target.value ? parseInt(e.target.value, 10) : null;
    setFormData((p) => ({ ...p, publicCompanyId: id }));
    if (id) {
      const c = publicCompanies.find((x) => x.id === id);
      if (c) setFormData((p) => ({ ...p, name: c.companyName ?? "" }));
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      const res = await PublicCompanyApi.create({
        companyName: newCompanyName.trim(),
      } as PublicCompanyCreateRequest);
      setPublicCompanies((prev) => [...prev, res.data]);
      setFormData((p) => ({
        ...p,
        publicCompanyId: res.data.id,
        name: res.data.companyName ?? "",
      }));
      setNewCompanyName("");
      setShowAddCompany(false);
    } catch {
      toast.error("Failed to add company");
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.eventId || !formData.name?.trim()) {
      toast.error("Event and Name are required.");
      return;
    }
    setSaving(true);
    try {
      let imageUrl = formData.sponsorImageUrl ?? "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const uploadRes = await CommonApi.addImageFile(fd);
        imageUrl = (uploadRes.data as { url?: string })?.url ?? "";
      }

      const payload: EventSponsor = {
        ...formData,
        eventId: formData.eventId!,
        name: formData.name!.trim(),
        memberId: sponsorType === "member" ? formData.memberId : null,
        publicCompanyId:
          sponsorType === "publicCompany" ? formData.publicCompanyId : null,
        sponsorImageUrl: imageUrl || null,
      } as EventSponsor;

      if (sponsor) {
        await EventSponsorApi.update(payload);
      } else {
        await EventSponsorApi.create(payload);
      }

      toast.success("Sponsor saved.");
      setShow(false);
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to save sponsor");
    } finally {
      setSaving(false);
    }
  };

  const getEventLabel = (ev: Event) => {
    const base = ev.name ?? "Unnamed";
    if (ev.parentEventName) {
      return `${base} (${ev.parentEventName})`;
    }
    return base;
  };

  return (
    <Modal show={show} size="2xl" onClose={() => setShow(false)} popup>
      <Modal.Header>{sponsor ? "Edit Sponsor" : "Add Sponsor"}</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event *
            </label>
            <Select
              value={formData.eventId ?? ""}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  eventId: parseInt(e.target.value, 10),
                }))
              }
              required
            >
              <option value="">Select event</option>
              {upcomingEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {getEventLabel(ev)}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sponsor source
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sponsorType"
                  checked={sponsorType === "member"}
                  onChange={() => setSponsorType("member")}
                  className="mr-2"
                />
                Member
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sponsorType"
                  checked={sponsorType === "publicCompany"}
                  onChange={() => setSponsorType("publicCompany")}
                  className="mr-2"
                />
                Public / External Company
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sponsorType"
                  checked={sponsorType === "custom"}
                  onChange={() => setSponsorType("custom")}
                  className="mr-2"
                />
                Custom (name only)
              </label>
            </div>
          </div>

          {sponsorType === "member" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Member
              </label>
              <Select
                value={formData.memberId ?? ""}
                onChange={handleMemberChange}
              >
                <option value="">Select member</option>
                {[...members]
                  .sort((a, b) =>
                    (a.name ?? "").localeCompare(b.name ?? "", undefined, {
                      sensitivity: "base",
                    })
                  )
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
              </Select>
            </div>
          )}

          {sponsorType === "publicCompany" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Public / External Company
              </label>
              {!showAddCompany ? (
                <div className="flex gap-2 items-center flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <Select
                      value={formData.publicCompanyId ?? ""}
                      onChange={handlePublicCompanyChange}
                      className="w-full"
                    >
                      <option value="">Select company</option>
                      {[...publicCompanies]
                        .sort((a, b) =>
                          (a.companyName ?? "").localeCompare(
                            b.companyName ?? "",
                            undefined,
                            { sensitivity: "base" }
                          )
                        )
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.companyName}
                          </option>
                        ))}
                    </Select>
                  </div>
                  <Button
                    type="button"
                    color="light"
                    onClick={() => setShowAddCompany(true)}
                    className="shrink-0"
                  >
                    + New
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2 items-center flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <TextInput
                      placeholder="Company name"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddCompany}
                    className="buttonMain shrink-0"
                  >
                    Add
                  </Button>
                  <Button
                    type="button"
                    color="gray"
                    onClick={() => setShowAddCompany(false)}
                    className="shrink-0"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <TextInput
              value={formData.name ?? ""}
              onChange={(e) =>
                setFormData((p) => ({ ...p, name: e.target.value }))
              }
              placeholder="Display name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sponsor Amount
            </label>
            <TextInput
              type="text"
              value={formData.sponsorAmount ?? ""}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9.]/g, "");
                setFormData((p) => ({
                  ...p,
                  sponsorAmount: v ? parseFloat(v) : null,
                }));
              }}
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Information
            </label>
            <Textarea
              value={formData.information ?? ""}
              onChange={(e) =>
                setFormData((p) => ({ ...p, information: e.target.value }))
              }
              rows={3}
              placeholder="Additional details..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sponsor Image
            </label>
            <FileInput
              accept="image/*"
              onChange={handleImageChange}
              helperText="Upload logo or sponsor image"
            />
            {formData.sponsorImageUrl && !file && (
              <div className="mt-2">
                <img
                  src={formData.sponsorImageUrl}
                  alt="Current"
                  className="h-16 object-contain border rounded"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              color="gray"
              onClick={() => setShow(false)}
              disabled={saving}
              className="shrink-0"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="buttonMain shrink-0"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
